import { Injectable } from "@angular/core";
import { SampleApiUrlUtil } from "@sharedcommon/util/sample-service.util";
import { Observable } from "rxjs";
import { SampleDashboardDto } from "@sharedcommon/model/sample/sample-dashboard.model";
import { NeqHttpClient } from "@sharedcommon/rest/http-client.config";
import { SampleDto } from "@sharedcommon/model/sample/sample.model";
import { SampleFilterDto } from "@sharedcommon/model/sample/filter/sample-filter.model";
import { SharedPagedResponse } from "@sharedcommon/model/shared-paged-response.model";
import { SamplePatchDto } from "@sharedcommon/model/sample-patch.model";
import { EventCreationDto } from "@sharedcommon/model/event-creation.model";

@Injectable({ providedIn: 'root'})
export class SampleRestService {
  constructor(private neqHttpClient: NeqHttpClient){}

  /**
   * Search sample
   * Response code 200 - Success
   * Response code 500 - Error
   * HTTP GET entity/{entityCode}/sample/search
   * Java method: com.neovia.services.sample.controller.EntityController.searchSamplesDashboardByEntityCode
   */
  searchSampleDashboardByEntityCode(entityCode: string, filterDto: SampleFilterDto, translated: boolean): Observable<SharedPagedResponse<SampleDashboardDto>> {
    return this.neqHttpClient.request({ method: 'GET', url: SampleApiUrlUtil.formatApiUrl(`entity/${entityCode}/sample/search`), data: { ...filterDto, translated: translated }});
  }

  /**
   * Search farm names present in sample
   * Response code 200 - Farm name present in samples which respond to the sample filter
   * Response code 500 - Error
   * HTTP GET entity/{entityCode}/sample/search-farm-name
   * Java method: com.neovia.services.sample.controller.EntityController.searchFarmNamesByEntityCode
   */
  searchFarmNameByEntityCode(entityCode: string, filterDto: SampleFilterDto): Observable<SharedPagedResponse<string>> {
    return this.neqHttpClient.request({ method: 'GET', url: SampleApiUrlUtil.formatApiUrl(`entity/${entityCode}/sample/search-farm-name`), data: filterDto });
  }

  /**
   * Search farm names present in sample
   * Response code 200 - Distributor present in samples which respond to the sample filter
   * Response code 500 - Error
   * HTTP GET entity/{entityCode}/sample/search-distributor
   * Java method: com.neovia.services.sample.controller.EntityController.searchDistributorsByEntityCode
   */
  searchDistributorByEntityCode(entityCode: string, filterDto: SampleFilterDto): Observable<SharedPagedResponse<string>> {
    return this.neqHttpClient.request({ method: 'GET', url: SampleApiUrlUtil.formatApiUrl(`entity/${entityCode}/sample/search-distributor`), data: filterDto });
  }

  /**
   * Search technicians present in sample
   * Response code 200 - Technicians present in samples which respond to the sample filter
   * Response code 500 - Error
   * HTTP GET entity/{entityCode}/sample/search-technician-name
   * Java method: com.neovia.services.sample.controller.EntityController.searchTechnicianNameByEntityCode
   */
  searchTechnicianByEntityCode(entityCode: string, filterDto: SampleFilterDto): Observable<SharedPagedResponse<string>> {
    return this.neqHttpClient.request({ method: 'GET', url: SampleApiUrlUtil.formatApiUrl(`entity/${entityCode}/sample/search-technician-name`), data: filterDto });
  }

  /**
   * Search species present in sample
   * Response code 200 - Species present in samples which respond to the sample filter
   * Response code 500 - Error
   * HTTP GET entity/{entityCode}/sample/search-species
   * Java method: com.neovia.services.sample.controller.EntityController.searchSpeciesByEntityCode
   */
  searchSpeciesByEntityCode(entityCode: string, filterDto: SampleFilterDto): Observable<SharedPagedResponse<string>> {
    return this.neqHttpClient.request({ method: 'GET', url: SampleApiUrlUtil.formatApiUrl(`entity/${entityCode}/sample/search-species`), data: filterDto });
  }

  /**
   * Get the sample by ID
   * Response code 200 - Success
   * Response code 404 - not found
   * Response code 500 - Error
   * HTTP GET sample/{sampleId}
   * Java method: com.neovia.services.sample.controller.SampleController.getSampleById
   */
  getSampleById(sampleId: string): Observable<SampleDto> {
    return this.neqHttpClient.request({ method: 'GET', url: SampleApiUrlUtil.formatApiUrl(`sample/${sampleId}`) });
  }

  /**
   * Create sample
   * Response code 201 - Success
   * Response code 500 - Error
   * HTTP POST sample
   * Java method: com.neovia.services.sample.controller.SampleController.save
   */
  create(sample: SampleDto, withEventCreation: boolean): Observable<SampleDto> {
    return this.neqHttpClient.request({ method: 'POST', url: SampleApiUrlUtil.formatApiUrl(`sample`), data: sample, queryParams: { createEvent: withEventCreation } });
  }

  /**
   * Update sample
   * Response code 200 - Success
   * Response code 500 - Error
   * HTTP PUT sample/{sampleId}
   * Java method: com.neovia.services.sample.controller.SampleController.putOrSaveCalculation
   */
  update(sampleId: string, sample: SampleDto, withEventCreation: boolean): Observable<SampleDto> {
    return this.neqHttpClient.request({ method: 'PUT', url: SampleApiUrlUtil.formatApiUrl(`sample/${sampleId}`), data: sample, queryParams: { createEvent: withEventCreation } });
  }

  /**
   * Update the sample status
   * Response code 200 - Success
   * Response code 404 - not found
   * Response code 423 - locked sample
   * Response code 500 - Error
   * HTTP PUT entity/{entityCode}/sample
   * Java method: com.neovia.services.sample.controller.EntityController.changeSampleStatus
   */
  updateSampleStatus(entityCode: string, sampleDashboard: SampleDashboardDto): Observable<SampleDashboardDto> {
    return this.neqHttpClient.request({ method: 'PUT', url: SampleApiUrlUtil.formatApiUrl(`entity/${entityCode}/sample/change-status`), data: sampleDashboard});
  }

  /**
   * Patch the sample
   * 
   * @param sampleId the sample ID to patch
   * @param patchDto the patch DTO for patching the sample 
   */
  patch(sampleId: string, patchDto: SamplePatchDto): Observable<void> {
    return this.neqHttpClient.request({ method: 'PATCH', url: SampleApiUrlUtil.formatApiUrl(`sample/${sampleId}`), data: patchDto});
  }

  /**
   * Patch all sample locked by the logged user (contained in the token)
   * Response code 204 - unlock success
   * Response code 500 - Error
   * HTTP PATCH sample/locked-by-me
   * Java method: com.neovia.services.sample.controller.SampleController.patchAllSampleByUser
   */
  patchAllSampleLockedByCurrentUser(patchDto: SamplePatchDto): Observable<void> {
    return this.neqHttpClient.request({ method: 'PATCH', url: SampleApiUrlUtil.formatApiUrl(`sample/locked-by-me`), data: patchDto});
  }

  /**
   * Launch creation of event linked to sample ID
   * @param sampleId      the sample ID from what the event must be linked
   * @param eventCreation the event information to create
   */
  createEventForSampleId(sampleId: string, eventCreation: EventCreationDto): Observable<void> {
    return this.neqHttpClient.request({ method: 'POST', url: SampleApiUrlUtil.formatApiUrl(`sample/${sampleId}/event`), data: eventCreation});
  }
}